import { Injectable } from '@angular/core';
import { Company, COMPANY_STATE } from '../interface/company';
import { MigrateDocument, NF_response } from '../interface/NF-response';
import { NFSE_response, ResponseRPS } from '../interface/NFSE-response';
import { INVOICE_STATE } from '../shared/lists/invoice-state';
import { BehaviorSubject, catchError, distinctUntilChanged, filter, first, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { SnackbarService } from './snackbar.service';
import { environment } from 'src/environments/environment';
import { DataService } from './data.service';
import { LayoutService } from './layout.service';
import { detItem55, NFe } from '../interface/NF-e';
import { AddressService } from './address.service';
import { ICertificateDigital } from '../interface/ICertificate-digital';
import { InvoiceRejections, MigrateService, SEFAZ_CODES } from './migrate.service';
import { MigrateQueryDocument, MigrateQueryResponse } from '../interface/fiscal/migrate-query-response';
import { ApiPaginatedInvoiceQueryParams, ApiPaginatedInvoiceResponse, PaginatedInvoice, PaginationService } from './pagination.service';
import { API_ERRORS } from '../shared/lists/errors';
import { AllInvoiceModels } from '@app/interface/mkgo-invoice';
import { ObjectId } from 't5-common';
import { NFCe } from '@app/interface/NFC-e';
import { FIN_NFE } from '@app/shared/enums/fiscal/fin-nfe';
import IIinvoiceListExpandedItemData from '@app/interface/IInvoiceListExpandedItemData';
import { BOOLEAN_TWO } from '@app/shared/common-enums/boolean-two';
import { CompanyService } from './company.service';
import { Utilities } from '@app/class/utilities';

interface CertificateData {
  lessThanTenDays: boolean;
  isValid: boolean;
  expireDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class MigrateCommonService {

  readonly URL = environment.migrate.URL;
  readonly NO_ACCESS_KEY = "_SEM_CHAVE_DE_ACESSO";

  public certificateData$ = new BehaviorSubject<CertificateData>({
    expireDate: undefined,
    isValid: false,
    lessThanTenDays: undefined
  });

  constructor(
    private http: HttpClient,
    private layout: LayoutService,
    private dataService: DataService,
    private snackbar: SnackbarService,
    private _addressService: AddressService,
    private _companyService: CompanyService
  ) {
    dataService.company$
      .pipe(
        distinctUntilChanged(),
        filter(company => company && company.migrateIntegrated),
        switchMap(company => this._getCertificateDigital(company))
      )
      .subscribe((data) => this.certificateData$.next(data));
  }


  private _getCertificateDigital(company: Company): Observable<CertificateData> {
    const url = `${environment.migrate.URL}/v1/company/certificate`;
    const formatCertificateData = (dueDate: string | Date, dueHour?: string): CertificateData => {
      const due = new Date(dueDate);
      const today = new Date().getTime();
      const dayExpire = due.getTime(); // format: "YYYY-MM-DD" or Date
      const tenDays = 864000000;

      let isValid = false;
      let lessThanTenDays = false;

      if ((dayExpire - today) <= tenDays) {
        lessThanTenDays = true;
        if ((dayExpire - today) <= 0) {
          lessThanTenDays = true;
          isValid = false;
        } else {
          isValid = true;
        }
      } else {
        isValid = true;
      }

      const dataFormatada = due.toLocaleDateString('pt-BR');
      const horaFormatada = dueHour
        ? dueHour.substring(0, 5)
        : due.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      return {
        expireDate: `${dataFormatada} às ${horaFormatada}`,
        isValid,
        lessThanTenDays: lessThanTenDays ? lessThanTenDays : undefined
      };
    };

    if (company.digitalCertificateConsult?.dueDate) {
      return of(formatCertificateData(company.digitalCertificateConsult.dueDate));
    }

    return this.dataService.httpOptions(company.cnpj)
      .pipe(
        switchMap(options => this._addressService.findState(company.address.state).pipe(
          map(state => ({ options, state }))
        )),
        switchMap(({ options, state }) => {
          options['params'] = new HttpParams().append('dhUF', state.uf);
          return this.http.get<ICertificateDigital>(url, options);
        }),
        map(response => {

          let digitalCertificateData: CertificateData = {
            expireDate: undefined,
            isValid: false,
            lessThanTenDays: undefined
          };

          if (response && response.Documentos?.length > 0) {
            const doc = response.Documentos[0];
            return formatCertificateData(doc.CertDtaVencimento, doc.CertHoraVencimento);
          }

          return digitalCertificateData;
        }),
        catchError(err => {
          console.error('Erro ao obter o certificado digital:', err);
          return of({
            isValid: false,
            lessThanTenDays: undefined,
            expireDate: undefined
          });
        })
      )
  }

  /**
   * @returns @interface NF_response or @interface NFSE_response
   * Type the return as one of then when u know the id for avoid typing errors
   */
  public getInvoiceById<T>(cnpj: string, invoiceId: string, transferId?: string): Observable<T> {
    const url = `${this.URL}/v1/invoice/${invoiceId}`;
    return this.dataService.httpOptions(cnpj).pipe(
      switchMap(options => {
        if (transferId) {
          options.params = (options.params || new HttpParams()).append('transferId', transferId);
        }
        return this.http.get<T>(url, options);
      }),
      first()
    )
  }

  /** Used to change just some properties of a invoice without send to Migrate */
  updateInvoiceProps(
    invoiceId: ObjectId,
    props: Partial<Omit<NF_response, 'id' | 'request' | 'response' | 'response2' | 'cnpj'>>,
    companyCnpj?: string
  ) {
    const url = `${this.URL}/v1/invoice/${invoiceId}`;
    return this.dataService.httpOptions(companyCnpj).pipe(
      switchMap(options => this.http.put(url, props, options)),
      first()
    )
  }

  public async getPaginatedInvoiceInvoiceRejectionsAndEvents(invoiceId: string) {
    const url = `${this.URL}/v1/invoice/events-and-rejections/${invoiceId}`;
    const options = await firstValueFrom(this.dataService.httpOptions(this.dataService.company.cnpj));
    const response = await firstValueFrom(this.http.get<IIinvoiceListExpandedItemData>(url, options));
    return response;
  }

  public fetchInvoices<T extends NFSE_response | NF_response>(cnpj: string, paginatedFilter: ApiPaginatedInvoiceQueryParams): Observable<T[]> {
    const url = `${this.URL}/v1/invoice`;
    let params = PaginationService.getParams(paginatedFilter);

    return this.dataService.httpOptions(cnpj, params)
      .pipe(
        switchMap(options => this.http.get<T[]>(url, options)),
        first(),
      )
  }

  public getPaginatedInvoices<T extends PaginatedInvoice>(cnpj: string, paginatedFilter: Omit<ApiPaginatedInvoiceQueryParams, "shouldReturnRequestAndResponse">): Observable<ApiPaginatedInvoiceResponse<T>> {
    const url = `${this.URL}/v1/invoice/page`;
    let params = PaginationService.getParams(paginatedFilter);

    return this.dataService.httpOptions(cnpj, params)
      .pipe(
        switchMap(options => this.http.get<ApiPaginatedInvoiceResponse<T>>(url, options)),
        first(),
        switchMap(resp => {
          if (paginatedFilter.all && resp.pagination.hasNext) {
            paginatedFilter.limit = resp.pagination.total;
            params = PaginationService.getParams(paginatedFilter);
            return this.dataService.httpOptions(cnpj, params).pipe(
              switchMap(options2 => this.http.get<ApiPaginatedInvoiceResponse<T>>(url, options2)),
              first()
            )
          } else {
            return of(resp)
          }
        }),
        catchError(err => {
          if (err.error.error === API_ERRORS.notFound) {
            return of<ApiPaginatedInvoiceResponse<T>>({
              invoices: [],
              pagination: {
                hasNext: true,
                hasPrev: false,
                limit: 5,
                page: 1,
                total: 0,
                totalPages: 0,
              }
            })
          } else {
            throw err;
          }
        }),
      )
  }

  private _getModel(model: AllInvoiceModels) {
    switch (model) {
      case "OIM":
      case "OIP":
      case "NFe-F":
      case "NFe":
        return "NFe"
      case "NFCe":
        return "NFCe"
      case "NFSE":
      case "OIS":
        return "NFSe"
    }
  }

  public async downloadInvoiceFile(args: {
    invoiceId: string,
    model: AllInvoiceModels,
    fileType: "xml" | "pdf",
    queryForEvent?: boolean,
    transferId?: string,
    companyCnpj?: string
  }): Promise<{ blob: Blob; filename: string }> {
    const model = this._getModel(args.model);
    const url = `${this.URL}/v1/invoice/${args.invoiceId}/${model}/${args.fileType}`;
    const mimeType = args.fileType === "xml" ? "application/xml" : "application/pdf";
    let params = PaginationService.getParams({ queryForEvent: !!args.queryForEvent });

    if (args.transferId) {
      params = params.append('transferId', args.transferId);
    }

    const options = await firstValueFrom(
      this.dataService.httpOptions(args.companyCnpj ?? this.dataService.company.cnpj, params, mimeType)
    );

    const response = await firstValueFrom(
      this.http.get(url, {
        ...options,
        responseType: 'blob',
        observe: 'response'
      })
    );

    const contentDisposition = response.headers.get('Content-Disposition') || response.headers.get('content-disposition');
    let filename = args.fileType === "xml" ? 'document.xml' : 'document.pdf';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
      if (filenameMatch && filenameMatch.length === 2) {
        filename = filenameMatch[1].replace(/\.(\w+)[_"\-. ]+$/, ".$1");
      }
    }

    return {
      blob: response.body!,
      filename: filename
    };
  }

  public async getAll_NF(company: Company, paginatedFilter?: Omit<ApiPaginatedInvoiceQueryParams, 'shouldReturnRequestAndResponse'>): Promise<NF_response[]> {
    // verify if this company is integrated with migrate
    try {
      const created = await this.isCompanyCreated(company);
      if (!created) {
        return []
      }
    } catch (error) {
      console.error(error)
      return []
    }

    let url = `${this.URL}/v1/invoice-mkgo/nfce`;

    let options: {
      headers: HttpHeaders;
      params?: HttpParams;
    };

    if (paginatedFilter) {
      const params = PaginationService.getParams(paginatedFilter);
      options = await firstValueFrom(this.dataService.httpOptions(company.cnpj, params));
    } else {
      options = await firstValueFrom(this.dataService.httpOptions(company.cnpj));
    }

    const resp = await firstValueFrom(this.http.get<NF_response[]>(url, options));

    return resp.filter(nf => {
      if (Boolean(nf.request?.Documento)) {
        return true;
      } else {
        console.warn(`A nota de id "${nf.id}" está oculta por não possuir a tag <Documento>`)
        return false;
      }
    }).map(nf => {
      let newDet = []
      Object.defineProperty(nf, "cnpj", {
        value: company.cnpj,
        writable: false,
        enumerable: false
      });
      if (nf.request.Documento.det && !Array.isArray(nf.request.Documento.det)) {
        // det need be a array
        nf.request.Documento.det = [nf.request.Documento.det];
      }
      nf.request.Documento.det?.slice().map(detItem => {
        let newItem = { ...detItem };
        /**
         * detItem.imposto.ICMS.ICMS00 = { ... }
         *
         * detItem.imposto.ICMS = { ... }
         */
        const icmsTag = detItem.imposto?.ICMS;
        if (icmsTag) {
          const keys = Object.keys(icmsTag);
          const childName = keys.find(key => key.startsWith('ICMS'));
          if (childName) {
            newItem['imposto']['ICMS'] = icmsTag[childName]
          }
        }

        const ipiTag = detItem.imposto?.IPI;
        if (ipiTag) {
          const keys = Object.keys(ipiTag);
          const childName = keys.find(key => key.startsWith('IPI'));
          if (childName) {
            let CSTIPI = ipiTag[childName];
            const subkey = Object.keys(CSTIPI).find(key => key.startsWith('IPI'));
            CSTIPI['CST_IPI'] = CSTIPI['subkey'];

            // xml.imposto.IPI.IPINT.CST
            (newItem as detItem55)['imposto']['IPI']['CSTIPI'] = CSTIPI
          }
        }

        newDet.push(newItem)
      })
      nf.request.Documento.det = newDet;
      return nf;
    }).reverse();
  }

  static isGerencial(invoiceState: INVOICE_STATE): boolean {
    return [
      INVOICE_STATE.GERENCIAL,
      INVOICE_STATE.GERENCIAL_DEVOLUTIONED,
      INVOICE_STATE.IMPORTED_VIA_XML,
      INVOICE_STATE.INPUT_WITHOUT_XML].includes(invoiceState);
  }

  static isDevolution(obj: NFe | NFCe) {
    return obj.ide.finNFe == FIN_NFE.DEVOLUCAO;
  }

  static getRejectionsNF(obj: NF_response, useResponse2 = false): InvoiceRejections {
    if (!obj) {
      // if invoice was not created, consider as rejection
      return { migrateCode: null, migrateDescription: null, sefazCode: null, sefazDescription: null };
    }

    if (!obj.response && !obj.response2) {
      return null;
    }

    if (MigrateCommonService.isGerencial(obj.state)) {
      // internal
      return null;
    }

    let migrateCode: InvoiceRejections['migrateCode'];
    let migrateDescription: InvoiceRejections['migrateDescription'];
    let sefazCode: InvoiceRejections['sefazCode'];
    let sefazDescription: InvoiceRejections['sefazDescription'];

    let response: NF_response['response'];

    if (useResponse2 && obj.response2) {
      response = obj.response2;
    } else {
      response = obj.response;
    }

    if (response?.Codigo) {
      migrateCode = response.Codigo;
      migrateDescription = response.Descricao;
      const [documento] = response?.Documentos ?? [];

      if (documento) {
        if ((documento as MigrateDocument).Situacao) {
          const nfResponse = documento as MigrateDocument;
          sefazCode = nfResponse.Situacao.SitCodigo;
          sefazDescription = nfResponse.Situacao.SitDescricao;
        } else {
          const queryResponse = documento as MigrateQueryDocument;
          sefazCode = queryResponse.DocSitCodigo;
          sefazDescription = queryResponse.DocSitDescricao;
        }
      }
    }

    if (migrateCode === 100 && sefazCode === SEFAZ_CODES.AUTORIZADO) {
      return null;
    }

    return {
      migrateCode,
      migrateDescription,
      sefazCode,
      sefazDescription,
    };
  }

  static getRejectionsNFS(obj: NFSE_response, useResponse2 = false): InvoiceRejections {
    if (!obj) {
      // if invoice was not created, consider as rejection
      return { migrateCode: null, migrateDescription: null, sefazCode: null, sefazDescription: null };
    }

    if (MigrateCommonService.isGerencial(obj.state)) {
      // internal
      return null
    }

    const response = useResponse2 ? obj.response2 : obj.response;
    let migrateCode: InvoiceRejections['migrateCode'];
    let migrateDescription: InvoiceRejections['migrateDescription'];
    let sefazCode: InvoiceRejections['sefazCode'];
    let sefazDescription: InvoiceRejections['sefazDescription'];

    if (response.Codigo) {
      migrateCode = Number(response.Codigo);
      migrateDescription = response.Descricao;
      const [documento] = response?.Documentos ?? [];

      if (documento) {
        if ((documento as ResponseRPS).Situacao) {
          const rpsResponse = documento as ResponseRPS;
          sefazCode = rpsResponse.Situacao.SitCodigo;
          sefazDescription = rpsResponse.Situacao.SitDescricao;
        } else {
          const queryResponse = documento as MigrateQueryDocument;
          sefazCode = queryResponse.DocSitCodigo;
          sefazDescription = queryResponse.DocSitDescricao;
        }
      }
    }

    if (migrateCode === 100 && sefazCode === SEFAZ_CODES.AUTORIZADO) {
      return null
    }

    return {
      migrateCode,
      migrateDescription,
      sefazCode,
      sefazDescription,
    }

  }

  static async getDevolutionOriginInvoice({ accessKey, migrateService, devolution, migrateCommonService, company }: {
    migrateService: MigrateService;
    migrateCommonService: MigrateCommonService;
    accessKey: string;
    devolution: NF_response;
    company: Company;
  }): Promise<NF_response> {
    let original: NF_response;

    if (devolution.devolutionOriginInvoiceId) {
      const invoice = await firstValueFrom(migrateCommonService.getInvoiceById<NF_response>(company.cnpj, devolution.devolutionOriginInvoiceId));

      if (invoice && (accessKey === invoice.response.Documentos[0].DocChaAcesso)) {
        original = invoice;
      }
    } else {
      const allNfInvoices = await migrateService.getAll_NF(company);

      original = allNfInvoices.find(nf => {
        if (nf.response['Documentos'] && (nf as NF_response).response.Documentos.length) {
          return accessKey === (nf as NF_response).response.Documentos[0].DocChaAcesso;
        } else return 0
      }) as any;
    }

    return original;
  }

  static getRejectionsOfConsult(consult: MigrateQueryResponse): InvoiceRejections {
    let migrateCode: InvoiceRejections['migrateCode'];
    let migrateDescription: InvoiceRejections['migrateDescription'];
    let sefazCode: InvoiceRejections['sefazCode'];
    let sefazDescription: InvoiceRejections['sefazDescription'];

    // read consult
    if (consult.Codigo) {
      migrateCode = consult.Codigo;
      migrateDescription = consult.Descricao;
    }
    if (consult.Documentos && consult.Documentos[0]) {
      const doc = consult.Documentos[0];
      if (doc.DocSitCodigo) {
        sefazCode = doc.DocSitCodigo;
        sefazDescription = doc.DocSitDescricao;
      }
    }
    if ((!migrateCode || migrateCode === 100) && sefazCode === SEFAZ_CODES.AUTORIZADO) {
      return null;
    }

    return {
      migrateCode,
      migrateDescription,
      sefazCode,
      sefazDescription
    };
  }


  async getAll_NFSE(company: Company, paginatedFilter?: Omit<ApiPaginatedInvoiceQueryParams, 'shouldReturnRequestAndResponse'>): Promise<NFSE_response[]> {
    try {
      // verify if this company is integrated with migrate
      const created = await this.isCompanyCreated(company);
      if (!created) {
        console.error('Company not created into invoice API')
        return []
      }
    } catch (error) {
      console.error(error)
      return [];
    }

    const url = `${this.URL}/v1/invoice-mkgo/nfse`;

    let options: {
      headers: HttpHeaders;
      params?: HttpParams;
    };

    if (paginatedFilter) {
      const params = PaginationService.getParams(paginatedFilter);
      options = await firstValueFrom(this.dataService.httpOptions(company.cnpj, params));
    } else {
      options = await firstValueFrom(this.dataService.httpOptions(company.cnpj));
    }

    const resp = await this.http.get(url, options).pipe(first()).toPromise();
    return resp as NFSE_response[];
  }

  public async isCompanyCreated(company: Company) {
    if (company.currentState === COMPANY_STATE.integrated) return;

    if (!company.existInIntegrateMkgoDatabase || company.existInIntegrateMkgoDatabase === BOOLEAN_TWO.FALSE) {
      let existent: any;

      try {
        existent = await this.getCompanyByCNPJ(company.cnpj);
      } catch (error) {
        console.error(error)
      } finally {
        company.existInIntegrateMkgoDatabase = !!existent ? BOOLEAN_TWO.TRUE : BOOLEAN_TWO.FALSE;
        await this._companyService.updateExistInIntegrateMkgoDatabase(company, company.existInIntegrateMkgoDatabase)
          .catch(err => console.error(err));
      }
    }

    if (company.existInIntegrateMkgoDatabase === BOOLEAN_TWO.FALSE) {
      let accessKey: string;
      let lastNSU: string;

      if (company.migrateIntegrated) {
        accessKey = company.accessKeyInvoice
        lastNSU = company.lastNSU;
        if (!accessKey) {
          this.layout.loader = false;
          accessKey = prompt('Digite a chave de acesso para integração');
        }
        if (!lastNSU) {
          this.layout.loader = false;
          lastNSU = prompt('Digite o último NSU para integração');
        }
      } else {
        accessKey = `${Utilities.removeMask(company.cnpj)}${this.NO_ACCESS_KEY}`;
        lastNSU = '1';
      }

      if (accessKey || lastNSU) {
        try {
          const newCompany = await this.registerCompany(accessKey, lastNSU, !company.migrateIntegrated);
          if (!newCompany) {
            this.snackbar.error('COMPANY.NOT_INTEGRATED');
          } else {
            company.existInIntegrateMkgoDatabase = BOOLEAN_TWO.TRUE;
            this.snackbar.success('COMPANY.INTEGRATED');
            await this._companyService.update(company);
          }
        } catch (error) {
          console.error({ error })
          this.snackbar.error('COMPANY.NOT_INTEGRATED');
        }
      }
    }

    return company.existInIntegrateMkgoDatabase === BOOLEAN_TWO.TRUE;
  }

  public async getCompanyByCNPJ(cnpj: string) {
    const url = `${this.URL}/v1/company`;
    const options = await firstValueFrom(this.dataService.httpOptions(cnpj));
    const resp = await this.http.get(url, options).pipe(first()).toPromise();
    return resp;
  }

  public async registerCompany(accessKey: string, lastNSU: string, gerencial: boolean) {
    const url = `${this.URL}/v1/company`;

    const options = await firstValueFrom(this.dataService.httpOptions(false))
    let body = {
      "accessKey": accessKey,
      "lastNSU": lastNSU.padStart(15, '0'),
    }

    body["status"] = gerencial ? 3 : 2;

    const resp = await this.http.post(url, body, options).pipe(first()).toPromise();
    return resp
  }

  /**
   * Update only the fields that are setted in the updateData object
   * @type type the function as NF_response or NFSE_response
   * @param cnpj company CNPF
   * @param invoiceId invoice ID
   * @param updateData fields to update (exclude: request and responses attributes)
   * @returns
   */
  public updateInvoiceById<T>(
    cnpj: string,
    invoiceId: string,
    updateData: {
      operationId?: string;
      devolutionOriginInvoiceId?: string;
      osId?: string;
      orderID?: string;
      state?: number;
      numero?: string;
      serie?: string;
      cnpjEmitente?: string;
      correctionLetter?: string;
      response2?: string;
      emitionDate?: string;
      isEntry?: boolean;
      isFiscal?: boolean;
      clientOrSupplierName?: string;
      refundOriginId?: string;
      refundTargetId?: string;
      refundBalanceRestoredAt?: string;
      value?: number;
      osNumber?: number;
      orderNumber?: number;
      clientId?: string;
      supplierId?: string;
      model?: AllInvoiceModels;
    }
  ): Observable<T> {
    const url = `${this.URL}/v1/invoice/${invoiceId}`;
    return this.dataService.httpOptions(cnpj).pipe(
      switchMap(options => this.http.put<any>(url, updateData, options)),
      first()
    );
  }
}
